import type { Config } from 'tailwindcss'
import defaultTheme from 'tailwindcss/defaultTheme'

const defaultSans = [
  'var(--font-sans)',
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  ...defaultTheme.fontFamily.sans,
]

const defaultSerif = [
  'var(--font-serif)',
  'ui-serif, serif',
  ...defaultTheme.fontFamily.serif,
]

const config = {
  content: ['./src/**/*.{js,ts,jsx,tsx,mdx}'],
  darkMode: ['selector'],
  theme: {
    container: {
      center: true,
      padding: '2rem',
      screens: {
        '2xl': '1400px',
        '3xl': '1920px',
      },
    },
    extend: {
      typography: (theme: any) => ({
        DEFAULT: {
          // This targets the default 'prose' class
          css: {
            color: '#262522',
            // Target inline code elements
            code: {
              color: theme('colors.red.600'), // Your desired reddish text color
              backgroundColor: theme('colors.gray.100'), // or gray.200 for slightly darker
              fontWeight: '400', // Use 'normal' or a specific weight like '400'
              paddingLeft: theme('spacing[1.5]'), // Adjust horizontal padding
              paddingRight: theme('spacing[1.5]'),
              paddingTop: theme('spacing.1'), // Adjust vertical padding
              paddingBottom: theme('spacing.1'),
              borderRadius: theme('borderRadius.md'), // Rounded corners
              // Ensure no backticks are added by ::before and ::after if react-markdown already provides them or if you don't want them
            },
            // Remove the default backticks added by prose
            'code::before': {
              content: '""', // Or set to `none` if that works better in your setup
            },
            'code::after': {
              content: '""', // Or set to `none`
            },
            b: {
              fontWeight: 'bold',
            },
            blockquote: {
              'border-inline-start-color': theme('colors.offblack'),
              'padding-inline-start': '0.75rem',
            },
            // You can add other prose customizations here if needed
            h2: {
              marginTop: '1em',
              marginBottom: '0.5em', // or your desired value
            },
            pre: {
              backgroundColor: theme('colors.gray.50'), // or gray.100 for a bit darker
              color: theme('colors.gray.900'),
            },
            ol: {
              'padding-inline-start': '1.25rem',
              li: {
                marginTop: '0em',
                'padding-inline-start': '0rem',
                '&::marker': {
                  color: theme('colors.gray.900'),
                },
                // Target last paragraph in list items
                '& > p:last-child': {
                  marginBottom: '0', // Remove bottom margin
                  marginTop: '0', // Adjust top margin as needed
                },
              },
            },
            ul: {
              'padding-inline-start': '1rem',
              li: {
                marginTop: '0em',
                'padding-inline-start': '0rem',
                '&::marker': {
                  color: theme('colors.gray.900'),
                },
                // Target last paragraph in list items
                '& > p:last-child': {
                  marginBottom: '0', // Remove bottom margin
                  marginTop: '0', // Adjust top margin as needed
                },
              },
            },
          },
        },
        // You can also define this for other prose variants like 'prose-lg' if you use them
        // lg: {
        //   css: {
        //     'code': { /* ... styles for prose-lg ... */ },
        //     'code::before': { content: '""' },
        //     'code::after': { content: '""' },
        //   }
        // }
      }),
      fontFamily: {
        sans: defaultSans,
        serif: defaultSerif,
        cta: ['var(--font-mono)'],
      },
      fontSize: {
        xxs: '0.625rem',
      },
      colors: {
        // shadcn colors
        border: 'hsl(var(--border))',
        input: 'hsl(var(--input))',
        ring: 'hsl(var(--ring))',
        background: 'hsl(var(--background))',
        foreground: 'hsl(var(--foreground))',
        primary: {
          DEFAULT: 'hsl(var(--primary))',
          foreground: 'hsl(var(--primary-foreground))',
        },
        secondary: {
          DEFAULT: 'hsl(var(--secondary))',
          foreground: 'hsl(var(--secondary-foreground))',
        },
        destructive: {
          DEFAULT: 'hsl(var(--destructive))',
          foreground: 'hsl(var(--destructive-foreground))',
        },
        muted: {
          DEFAULT: 'hsl(var(--muted))',
          foreground: 'hsl(var(--muted-foreground))',
        },
        accent: {
          DEFAULT: 'hsl(var(--accent))',
          foreground: 'hsl(var(--accent-foreground))',
        },
        popover: {
          DEFAULT: 'hsl(var(--popover))',
          foreground: 'hsl(var(--popover-foreground))',
        },
        card: {
          DEFAULT: 'hsl(var(--card))',
          foreground: 'hsl(var(--card-foreground))',
        },
        // branded colors
        overstock: 'hsl(var(--overstock))',
        // custom colors
        offblack: 'hsl(var(--offblack))',
        darkest: 'hsl(var(--darkest))',
        darkbg: 'hsl(var(--darkbg))',
        darkborder: 'hsl(var(--darkborder))',
        darkinputlabel: 'hsl(var(--darkinputlabel))',
        darkinput: 'hsl(var(--darkinput))',
        picks: 'hsl(var(--picks))',
        picksbg: 'hsl(var(--picksbg))',
        dupe: 'hsl(var(--dupe))',
        dupedark: 'hsl(var(--dupe-dark))',
        dupelight: 'hsl(var(--dupe-light))',
        bellblue: 'hsl(var(--dupe))',
        notice: 'hsl(var(--notice))',
        press: 'hsl(var(--press))',
        blog: 'hsl(var(--blog))',
        promo: 'hsl(var(--promo))',
        secondhand: 'hsl(var(--secondhand))',
        inputgray: 'hsl(var(--inputgray))',
        blue: {
          DEFAULT: 'hsl(var(--blue))',
          50: '#84B5FF',
          100: '#6FA9FF',
          200: '#4690FF',
          300: '#1E78FF',
          400: '#0061F4',
          500: 'hsl(var(--blue))',
          600: '#003B93',
          700: '#00245B',
          800: '#000E23',
          900: '#000000',
          950: '#000000',
        },
        orange: {
          DEFAULT: 'hsl(var(--orange))',
          50: '#FFE8E1',
          100: '#FFD9CC',
          200: '#FFBAA3',
          300: '#FF9B7B',
          400: '#FF7D52',
          500: 'hsl(var(--orange))',
          600: '#F03B00',
          700: '#B82E00',
          800: '#802000',
          900: '#481200',
          950: '#2C0B00',
        },
        yellow: {
          DEFAULT: 'hsl(var(--yellow))',
          50: '#FFFDF0',
          100: '#FFFCE0',
          200: '#FFF9C2',
          300: '#FFF6A8',
          400: '#FFF38A',
          500: 'hsl(var(--yellow))',
          600: '#FFE924',
          700: '#DBC500',
          800: '#8F8100',
          900: '#474000',
          950: '#242000',
        },
      },
      borderRadius: {
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 6px)',
      },
      spacing: {
        // This value comes from the CSS variable --sidebar-width-collapsed in global.css
        'sidebar-collapsed': 'var(--sidebar-width-collapsed)',
      },
      keyframes: {
        'caret-blink': {
          '0%,70%,100%': { opacity: '1' },
          '20%,50%': { opacity: '0' },
        },
        border: {
          to: { '--border-angle': '360deg' },
        },
        progress: {
          '0%': { transform: ' translateX(0) scaleX(0)' },
          '40%': { transform: 'translateX(0) scaleX(0.4)' },
          '100%': { transform: 'translateX(100%) scaleX(0.5)' },
        },
        fade: {
          '0%, 100%': { opacity: '0' },
          '50%': { opacity: '1' },
        },
        fadeInSlideDown: {
          '0%': { opacity: '0', transform: 'translateY(-10px)' },
          '100%': { opacity: '1', transform: 'translateY(0)' },
        },
        flash: {
          0: { opacity: '0' },
          '30%': { opacity: '1' },
          '60%': { opacity: '0' },
          '100%': { opacity: '0' },
        },
        'accordion-down': {
          from: { height: '0' },
          to: { height: 'var(--radix-accordion-content-height)' },
        },
        'accordion-up': {
          from: { height: 'var(--radix-accordion-content-height)' },
          to: { height: '0' },
        },
        'bounce-x': {
          '0%, 100%': { transform: 'translateX(-1.5px)' },
          '50%': { transform: 'translateX(1.5px)' },
        },
        'bounce-y': {
          '0%, 100%': { transform: 'translateY(-1.5px)' },
          '50%': { transform: 'translateY(1.5px)' },
        },
        'shiny-text': {
          '0%, 90%, 100%': {
            'background-position': 'calc(-100% - var(--shiny-width)) 0',
          },
          '30%, 60%': {
            'background-position': 'calc(100% + var(--shiny-width)) 0',
          },
        },
        'fade-pulse': {
          '0%, 25%, 100%': { opacity: '0' },
          '50%, 75%': { opacity: '1' },
        },
        'pulse-once': {
          '0%, 100%': { opacity: '0' },
          '45%, 50%, 65%': { opacity: '1' },
        },
        // ProShimmerButton component animations and transitions (using colors in global.css)
        'shimmer-slide': {
          to: {
            transform: 'translate(calc(100cqw - 100%), 0)',
          },
        },
        'spin-around': {
          '0%': {
            transform: 'translateZ(0) rotate(0)',
          },
          '15%, 35%': {
            transform: 'translateZ(0) rotate(90deg)',
          },
          '65%, 85%': {
            transform: 'translateZ(0) rotate(270deg)',
          },
          '100%': {
            transform: 'translateZ(0) rotate(360deg)',
          },
        },
        scroll: {
          to: {
            transform: 'translate(calc(-50% - 0.5rem))',
          },
        },
      },
      animation: {
        'caret-blink': 'caret-blink 1.25s ease-out infinite',
        border: 'border 4s linear infinite',
        progress: 'progress 1s infinite linear',
        fade: 'fade 1s ease-in-out infinite',
        fadeInSlideDown: 'fadeInSlideDown 0.3s ease-out',
        flash: 'fade 550ms ease-in-out forwards',
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'bounce-x': 'bounce-x 1s ease-in-out infinite',
        'bounce-y': 'bounce-y 1s ease-in-out infinite',
        'shiny-text': 'shiny-text 4s infinite',
        'fade-pulse':
          'fade-pulse var(--pulse-duration, 10000ms) ease-in-out infinite',
        'pulse-once': 'pulse-once 3s cubic-bezier(0.4, 0, 0.6, 1) forwards',
        'shimmer-slide':
          'shimmer-slide var(--speed) ease-in-out infinite alternate',
        'spin-around': 'spin-around calc(var(--speed) * 2) infinite linear',
        scroll:
          'scroll var(--animation-duration, 40s) var(--animation-direction, forwards) linear infinite',
      },
      transformOrigin: {
        'left-right': '0% 50%',
      },
      transitionDuration: {
        '3000': '3000ms',
        '5000': '5000ms',
        '7000': '7000ms',
        '10000': '10000ms',
      },
      transitionDelay: {
        '1200': '1200ms',
        '1500': '1500ms',
        '1800': '1800ms',
        '2000': '2000ms',
        '2500': '2500ms',
      },
      screens: {
        '2xl': '1536px',
        '3xl': '1920px',
        '4xl': '2560px',
      },
    },
  },
  plugins: [require('@tailwindcss/typography'), require('tailwindcss-animate')],
} satisfies Config

export default config
